// Third Party
import { filter, findIndex } from 'lodash';
import * as React from 'react';
import ReactDOM from 'react-dom';
import { Transition, TransitionChild } from '@headlessui/react';

// Utils
import { Feature, useSearch } from '@contexts/search-context';
import { isSSR } from '@utils/predicates';
import { PropertyType, UniquePropertyType } from '@gql';

// UI
import { Typography } from '@components/typography';
import { Button } from '@components/button';
import { ButtonIcon } from '@components/button-icon';
import { Icon, IconNames } from '@components/icon';
import { CapacitySelector } from './features-modal/capacity-selector';
import { PropertyTypeSelection } from './features-modal/property-type-selection';
import { UniquePropertyTypeSelection } from './features-modal/unique-property-type-selection';
import { AmenitiesSelection } from './features-modal/amenities-selection';
import { Input } from '@components/form/input';

type Props = { viewAllProperties: (filters: Feature[]) => void };

export const FeaturesModal: React.FC<Props> = ({ viewAllProperties }) => {
  const allPropertyTypes = [
    PropertyType.House,
    PropertyType.Apartment,
    PropertyType.BedAndBreakfast,
    PropertyType.GuestHouse,
    PropertyType.BoutiqueHotel,
  ];

  const allUniquePropertyTypes = [
    UniquePropertyType.Barn,
    UniquePropertyType.Boat,
    UniquePropertyType.Bungalow,
    UniquePropertyType.Cabin,
    UniquePropertyType.Campsite,
    UniquePropertyType.Castle,
    UniquePropertyType.Cottage,
    UniquePropertyType.HistoricHotel,
    UniquePropertyType.Houseboat,
    UniquePropertyType.Hut,
    UniquePropertyType.Lighthouse,
    UniquePropertyType.NatureLodge,
    UniquePropertyType.GlampingTent,
    UniquePropertyType.TinyHome,
    UniquePropertyType.Villa,
  ];

  const {
    activeFilters,
    allFeatures,
    deactivateFeaturesModal,
    featuresModalActive,
    minNumberOfBathrooms,
    setMinNumberOfBathrooms,
    minNumberOfRooms,
    setMinNumberOfRooms,
    minPrice,
    setMinPrice,
    maxPrice,
    setMaxPrice,
    propertyTypes,
    setPropertyTypes,
    uniquePropertyTypes,
    setUniquePropertyTypes,
    checkInDate,
    checkOutDate,
  } = useSearch();

  const hasSearchDates = checkInDate !== null && checkOutDate !== null;

  const [selectedActiveFeatures, setSelectedActiveFeatures] =
    React.useState<Feature[]>(activeFilters);

  React.useEffect(() => {
    setSelectedActiveFeatures(activeFilters);
  }, [activeFilters]);

  const toggleFeatureFilter = (featureFilter: Feature): void => {
    const i = findIndex(
      selectedActiveFeatures,
      (o) => o.key == featureFilter.key
    );

    if (i > -1) {
      setSelectedActiveFeatures(
        filter(selectedActiveFeatures, (f) => f.key !== featureFilter.key)
      );
    } else {
      setSelectedActiveFeatures([...selectedActiveFeatures, featureFilter]);
    }
  };

  const togglePropertyType = (propertyType: PropertyType): void => {
    const i = findIndex(propertyTypes, (o) => o == propertyType);

    if (i > -1) {
      setPropertyTypes(filter(propertyTypes, (f) => f !== propertyType));
    } else {
      setPropertyTypes([...propertyTypes, propertyType]);
    }
  };

  const toggleUniquePropertyType = (
    uniquePropertyType: UniquePropertyType
  ): void => {
    const i = findIndex(uniquePropertyTypes, (o) => o == uniquePropertyType);

    if (i > -1) {
      setUniquePropertyTypes(
        filter(uniquePropertyTypes, (f) => f !== uniquePropertyType)
      );
    } else {
      setUniquePropertyTypes([...uniquePropertyTypes, uniquePropertyType]);
    }
  };

  const clearSelectedFeatures = (): void => {
    setPropertyTypes([]);
    setUniquePropertyTypes([]);
    setMinNumberOfRooms(0);
    setMinNumberOfBathrooms(0);
    setSelectedActiveFeatures([]);
    setMinPrice(null);
    setMaxPrice(null);
  };

  const [showUniquePropertyTypes, setShowUniquePropertyTypes] =
    React.useState(false);

  const handleEscapeKeyPress = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      closeModal();
    }
  };

  const closeModal = (): void => {
    viewAllProperties(selectedActiveFeatures);
    deactivateFeaturesModal();
  };

  // If modal is active add class to body to prevent scroll
  React.useEffect(() => {
    if (featuresModalActive) {
      document.body.classList.add('modalActive');
      document.addEventListener('keyup', handleEscapeKeyPress);
    } else {
      document.body.classList.remove('modalActive');
      document.removeEventListener('keyup', handleEscapeKeyPress);
    }
  }, [featuresModalActive]);

  if (!isSSR) {
    return ReactDOM.createPortal(
      <Transition
        show={featuresModalActive}
        as="div"
        className="fixed inset-0 h-screen overflow-hidden sm:px-8"
        style={{ zIndex: 2000 }}
      >
        <div className="flex items-end justify-center sm:px-4 text-center sm:block sm:p-0 h-screen sm:h-5/6 sm:max-h-[1082px]">
          <TransitionChild
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 transition-opacity">
              <div className="absolute inset-0 bg-gray-main opacity-75"></div>
            </div>
          </TransitionChild>
          {/* This element is to trick the browser into centering the modal contents. */}
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen"></span>
          &#8203;
          <TransitionChild
            as="div"
            className="fixed top-24 bottom-0 sm:relative sm:top-auto sm:bottom-auto inline-block align-bottom max-w-6xl text-left bg-white sm:h-5/6 overflow-hidden rounded-t-lg sm:rounded-lg shadow-xl transform transition-all sm:align-middle w-full lg:w-3/4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-headline"
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="flex flex-col justify-between h-full text-primary-main">
              <div className="w-full px-4 items-center py-3 bg-white border-b border-gray-light flex justify-between">
                <div className="w-10 h-1" />
                <Typography variant="title4">Filters</Typography>
                <ButtonIcon
                  variant="text"
                  icon={IconNames.X}
                  color="gray"
                  size="md"
                  isCircle={true}
                  ariaDescribedby="Close Filter Modal"
                  onClick={closeModal}
                />
              </div>
              <div className="px-4 py-2 sm:px-8 sm:py-6 bg-white overflow-auto">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-left sm:mt-0 sm:text-left w-full space-y-8">
                    {/* Pricing */}
                    {hasSearchDates && (
                      <div>
                        <Typography
                          variant="title5"
                          variantM="title3"
                          component="h2"
                        >
                          Price
                        </Typography>

                        <Typography
                          variant="body1"
                          variantM="body1"
                          component="p"
                          className="mt-4"
                        >
                          Nightly prices before fees and taxes
                        </Typography>

                        <div className="mt-4 flex flex-row space-x-4">
                          <Input
                            className="w-[300px]"
                            inputMode="numeric"
                            label="Min"
                            name="minPrice"
                            leftContent={
                              <Icon name={IconNames.SQUARE_DOLLAR} />
                            }
                            type="number"
                            value={minPrice || ''}
                            onChange={(e) => {
                              const castedValue = parseFloat(e.target.value);

                              setMinPrice(castedValue);
                            }}
                          />
                          {/* <span className="text-gray-dark pt-3"> to </span> */}

                          <Input
                            className="w-[300px]"
                            inputMode="numeric"
                            label="Max"
                            name="maxPrice"
                            leftContent={
                              <Icon name={IconNames.SQUARE_DOLLAR} />
                            }
                            type="number"
                            value={maxPrice || ''}
                            onChange={(e) => {
                              const castedValue = parseFloat(e.target.value);

                              setMaxPrice(castedValue);
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Rooms and Bathrooms */}
                    <div>
                      <Typography
                        variant="title5"
                        variantM="title3"
                        component="h2"
                      >
                        Rooms and Bathrooms
                      </Typography>
                      <div className="mt-4 flex flex-col whitespace-nowrap space-y-4">
                        <CapacitySelector
                          title="Bedrooms"
                          capacity={minNumberOfRooms}
                          maxCapacity={8}
                          onCapacityUpdate={(capacity) => {
                            setMinNumberOfRooms(capacity);
                          }}
                        />

                        <CapacitySelector
                          title="Bathrooms"
                          capacity={minNumberOfBathrooms}
                          maxCapacity={8}
                          onCapacityUpdate={(capacity) => {
                            setMinNumberOfBathrooms(capacity);
                          }}
                        />
                      </div>
                    </div>

                    {/* Property Type */}
                    <div>
                      <Typography
                        variant="title5"
                        variantM="title3"
                        component="h2"
                      >
                        Property Type
                      </Typography>
                      <PropertyTypeSelection
                        currentPropertyTypes={propertyTypes}
                        allPropertyTypes={allPropertyTypes}
                        togglePropertyTypeFilter={togglePropertyType}
                      />
                    </div>

                    {/* Unique Property Type */}
                    {showUniquePropertyTypes && (
                      <div>
                        <Typography
                          variant="title5"
                          variantM="title3"
                          component="h2"
                        >
                          Unique Property Type
                        </Typography>
                        <UniquePropertyTypeSelection
                          currentActiveUniquePropertyTypes={uniquePropertyTypes}
                          allUniquePropertyTypes={allUniquePropertyTypes}
                          toggleUniquePropertyTypesFilter={
                            toggleUniquePropertyType
                          }
                        />
                      </div>
                    )}
                    <div className="flex w-full justify-end">
                      <Button
                        className="-mt-4"
                        variant="text"
                        color="primary"
                        size="sm"
                        onClick={() =>
                          setShowUniquePropertyTypes(!showUniquePropertyTypes)
                        }
                      >
                        <Icon
                          className="pr-2"
                          name={
                            !showUniquePropertyTypes
                              ? IconNames.CHEVRON_DOWN
                              : IconNames.CHEVRON_UP
                          }
                        />
                        {!showUniquePropertyTypes ? 'see more' : 'see less'}
                      </Button>
                    </div>

                    {/* Amenities */}
                    <div>
                      <Typography
                        variant="title5"
                        variantM="title3"
                        component="h2"
                      >
                        {selectedActiveFeatures.length > 0
                          ? `Amenities & Features ( ${selectedActiveFeatures.length} )`
                          : 'Amenities & Features'}
                      </Typography>
                      <AmenitiesSelection
                        currentActiveFeatures={selectedActiveFeatures}
                        allFeatures={allFeatures}
                        toggleFeatureFilter={toggleFeatureFilter}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-between px-4 py-5 bg-white border-t border-gray-light sm:px-6 shadow-t-lg rounded-t-lg">
                <Button
                  variant={'text'}
                  onClick={() => {
                    clearSelectedFeatures();
                  }}
                >
                  Clear
                </Button>
                <Button
                  variant={'outlined'}
                  onClick={() => {
                    viewAllProperties(selectedActiveFeatures);
                    deactivateFeaturesModal();
                  }}
                >
                  View Properties
                </Button>
              </div>
            </div>
          </TransitionChild>
        </div>
      </Transition>,
      document.getElementById('modal-root') as Element
    );
  } else {
    return null;
  }
};
